// Единый чёрный список: записи, сверка, журнал сверок, выгрузки мессенджера и предложения.
// Формы — зеркало contracts/openapi/bark-hiring.v1.yaml.
//
// Три вещи, которые здесь не перепутать:
//
// 1. **Снятие и уничтожение — разные действия.** Снятие (`lift`) оставляет запись со всей
//    историей и добавляет дату, автора и причину. Уничтожение (`erasure`) стирает
//    персональные данные насовсем, по требованию человека, и вернуть их нечем. На экране
//    разница обязана быть видна, поэтому и в коде это два разных вызова с разными правами.
// 2. **Сверка ничего не решает, а показывает.** `check` отвечает успехом всегда — это показ
//    результата. Блокировка живёт на соседнем маршруте `gate`, и решение по совпадению
//    принимает ответственный, а не система (тз 5.8.2).
// 3. **Разбор выгрузки даёт ЧЕРНОВИКИ и только их.** В список отсюда не попадает никто:
//    единственный путь из черновика в запись — решение человека (`drafts/{id}/decision`).

import { request } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { ListResponse } from '@/shared/api/types';

import type { CheckResult } from './candidates';

const BASE = '/api/v1/hiring';

/** contracts/enums/statuses.yaml, blacklist_reason. «Иное» требует уточнения текстом. */
export const BLACKLIST_REASONS = [
  'theft',
  'intoxication',
  'absence',
  'gross_violation',
  'client_conflict',
  'other',
] as const;

export type BlacklistReason = (typeof BLACKLIST_REASONS)[number];

export type BlacklistStatus = 'active' | 'lifted';

export type DraftVerdict = 'pending' | 'to_blacklist' | 'note_only' | 'discarded';

/** На каком шаге сверяли: приём отклика, ручная проверка, оформление. */
export type CheckStage = 'application' | 'manual' | 'hire';

// ── Запись списка ─────────────────────────────────────────────────────────────────────────

/** Карточка записи. Персональные поля пусты, если данные уже уничтожены. */
export interface BlacklistEntryOut {
  id: string;
  status: BlacklistStatus;
  status_title: string;
  reason: string;
  reason_title: string;
  reason_note?: string | null;
  legal_basis: string;
  retention_until: string;
  created_at: string;
  added_by?: string | null;
  added_by_name?: string | null;
  last_name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  birth_date?: string | null;
  phone_normalized?: string | null;
  passport_series?: string | null;
  passport_number?: string | null;
  guard_license_no?: string | null;
  region?: string | null;
  legal_entity_id?: string | null;
  former_employee_id?: string | null;
  lifted_at?: string | null;
  lifted_by?: string | null;
  lifted_by_name?: string | null;
  lifted_reason?: string | null;
}

export interface BlacklistEntryIn {
  reason: BlacklistReason;
  /** Правовое основание внесения. Указывает заказчик, система не придумывает. */
  legal_basis: string;
  /** Обязательно при причине «иное»: справочник без пояснения пуст. */
  reason_note?: string | null;
  last_name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  birth_date?: string | null;
  phone?: string | null;
  passport_series?: string | null;
  passport_number?: string | null;
  guard_license_no?: string | null;
  region?: string | null;
  legal_entity_id?: string | null;
  former_employee_id?: string | null;
  /** По умолчанию три года от внесения (настройка ядра). */
  retention_until?: string | null;
}

export interface BlacklistQuery {
  status?: string;
  region?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export function listBlacklist(
  params: BlacklistQuery,
  signal?: AbortSignal,
): Promise<ListResponse<BlacklistEntryOut>> {
  return request(`${BASE}/blacklist${queryString({ ...params })}`, { signal });
}

export function getBlacklistEntry(id: string, signal?: AbortSignal): Promise<BlacklistEntryOut> {
  return request(`${BASE}/blacklist/${id}`, { signal });
}

export function createBlacklistEntry(
  body: BlacklistEntryIn,
  idempotencyKey: string,
): Promise<BlacklistEntryOut> {
  return request(`${BASE}/blacklist`, { method: 'POST', body, idempotencyKey });
}

/** Снятие: запись остаётся, к ней добавляются дата, автор и причина. */
export function liftBlacklistEntry(id: string, reason: string): Promise<BlacklistEntryOut> {
  return request(`${BASE}/blacklist/${id}/lift`, { method: 'POST', body: { reason } });
}

/** Уничтожение по требованию человека. Настоящее удаление, а не пометка. */
export function eraseBlacklistEntry(id: string, reason: string): Promise<Record<string, unknown>> {
  return request(`${BASE}/blacklist/${id}/erasure`, { method: 'POST', body: { reason } });
}

// ── Сверка ────────────────────────────────────────────────────────────────────────────────

export interface BlacklistCheckIn {
  candidate_id?: string | null;
  last_name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  birth_date?: string | null;
  phone?: string | null;
  passport_series?: string | null;
  passport_number?: string | null;
  guard_license_no?: string | null;
  region?: string | null;
  stage?: CheckStage;
}

/** Результат сверки словами и кодами. Каждая сверка уже записана в журнал. */
export interface BlacklistCheckOut {
  result: CheckResult;
  result_title: string;
  stage: string;
  /** Точное совпадение: дальше кандидата не двигают без решения ответственного. */
  blocks_promotion: boolean;
  /** Возможное совпадение: на оформлении нужно явное подтверждение. */
  confirmation_required: boolean;
  checked_at: string;
  check_log_id: string;
  message: string;
  next_action: string;
  candidate_id?: string | null;
  entry_id?: string | null;
  signal?: string | null;
  signal_title?: string | null;
  all_signals?: string[];
  reason?: string | null;
  reason_title?: string | null;
  reason_note?: string | null;
}

/** Дверь открыта. Закрытая дверь приезжает отказом с причиной и следующим шагом. */
export interface BlacklistGateOut {
  allowed: boolean;
  check: BlacklistCheckOut;
  confirmed?: boolean;
  message: string;
  next_action: string;
}

/** Строка журнала сверок: почему человека не пустили и кто это видел. */
export interface BlacklistCheckLogOut {
  id: string;
  result: string;
  result_title: string;
  stage: string;
  stage_title: string;
  created_at: string;
  candidate_id?: string | null;
  entry_id?: string | null;
  checked_by?: string | null;
  checked_by_name?: string | null;
  signal?: string | null;
  signal_title?: string | null;
}

export function checkBlacklist(body: BlacklistCheckIn): Promise<BlacklistCheckOut> {
  return request(`${BASE}/blacklist/check`, { method: 'POST', body });
}

export function gateCandidate(
  candidateId: string,
  options: { stage?: CheckStage; confirmed?: boolean } = {},
): Promise<BlacklistGateOut> {
  return request(`${BASE}/blacklist/gate`, {
    method: 'POST',
    body: {
      candidate_id: candidateId,
      stage: options.stage ?? 'hire',
      confirmed: options.confirmed ?? false,
    },
  });
}

export function listChecks(
  params: { candidate_id?: string; limit?: number },
  signal?: AbortSignal,
): Promise<ListResponse<BlacklistCheckLogOut>> {
  return request(`${BASE}/blacklist/checks${queryString({ ...params })}`, { signal });
}

// ── Выгрузки мессенджера и предложения ────────────────────────────────────────────────────

/** violators — группа «Нарушители», candidates — группа «Кандидаты». */
export type MessengerGroup = 'violators' | 'candidates';

export type ImportStatus = 'uploaded' | 'parsed' | 'reviewed' | 'failed';

/** Состояние разбора выгрузки. Ни одна запись отсюда в список сама не попадает. */
export interface MessengerImportOut {
  id: string;
  group_kind: MessengerGroup;
  status: ImportStatus;
  messages_total: number;
  drafts_total: number;
  /** Сколько черновиков ещё ждут решения человека. */
  pending: number;
  created_at: string;
  file_id?: string | null;
  started_by?: string | null;
  started_by_name?: string | null;
  erase_after?: string | null;
  /** Что модель предложила, а проверка отбросила. */
  protocol?: string | null;
}

/** Предложение в список. Пока решения нет, это не запись, а строка на другом столе. */
export interface BlacklistDraftOut {
  id: string;
  verdict: DraftVerdict;
  verdict_title: string;
  created_at: string;
  import_id?: string | null;
  source_employee_id?: string | null;
  raw_text?: string | null;
  full_name?: string | null;
  phone_normalized?: string | null;
  suggested_reason?: string | null;
  suggested_reason_title?: string | null;
  model_note?: string | null;
  entry_id?: string | null;
  decided_by?: string | null;
  decided_by_name?: string | null;
  decided_at?: string | null;
}

export interface DraftDecisionIn {
  verdict: 'to_blacklist' | 'note_only' | 'discarded';
  /** Обязательно при решении «в чёрный список». */
  legal_basis?: string | null;
  /** Пусто — берётся причина, предложенная моделью. */
  reason?: BlacklistReason | null;
  reason_note?: string | null;
  region?: string | null;
}

export function listImports(signal?: AbortSignal): Promise<ListResponse<MessengerImportOut>> {
  return request(`${BASE}/blacklist/imports`, { signal });
}

export function createImport(
  groupKind: MessengerGroup,
  fileId: string | null,
): Promise<MessengerImportOut> {
  return request(`${BASE}/blacklist/imports`, {
    method: 'POST',
    body: { group_kind: groupKind, file_id: fileId },
  });
}

export function parseImport(importId: string, text: string | null): Promise<MessengerImportOut> {
  return request(`${BASE}/blacklist/imports/${importId}/parse`, {
    method: 'POST',
    body: { text },
  });
}

export function listDrafts(
  params: { import_id?: string; verdict?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
): Promise<ListResponse<BlacklistDraftOut>> {
  return request(`${BASE}/blacklist/drafts${queryString({ ...params })}`, { signal });
}

export function decideDraft(draftId: string, body: DraftDecisionIn): Promise<BlacklistDraftOut> {
  return request(`${BASE}/blacklist/drafts/${draftId}/decision`, { method: 'POST', body });
}
